import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { head } from 'lodash';

import { useCreateModal } from 'partial/components/Modal';
import { usePersistState, removeEmpty, aclHelper } from 'helper';

import List from 'partial/components/List';
import Pagination from 'partial/components/Pagination';
import ThumbIcon from 'partial/components/ThumbIcon';
import Button from 'partial/components/Button';
import { Plus } from '@phosphor-icons/react';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import AddUpdateUser from './modals/AddUpdateUser';
import UserDetails from './container/UserDetails';
import StatusFilter from './components/StatusFilter';

import { useUsers } from './hooks';

const UserManagement = () => {
  const { setSubLabel } = useModuleContext();

  const navigate = useNavigate();
  const createModal = useCreateModal();
  const [filter, setFilter] = useState({ q: '', is_active: '' });
  const [selected, setSelected] = usePersistState('user-uuid', '', {
    keepOnUnmount: false,
  });
  const [isLoading, users, meta] = useUsers(removeEmpty(filter));

  const id = head(users)?.uuid;

  const onSubSelect = (item) => {
    setSelected(item?.uuid);
    navigate(`${item?.uuid}`);
  };

  const handleAddUser = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdateUser onClose={onClose} />,
    });
  }, [createModal]);

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} user${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="min-h-[calc(100vh_-_12rem)] flex bg-white border border-neutral-200 rounded-md flex-grow overflow-hidden">
      {/* Rail: search at the top, Add pinned in the footer above the count. */}
      <div className="flex flex-col border-r border-neutral-200 w-[300px] flex-shrink-0">
        <div className="p-3 border-b border-neutral-200">
          <StatusFilter
            name="q"
            value={filter?.q}
            onChange={setFilter}
            inputClassName="!w-full"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <List
            data={users}
            isLoading={isLoading}
            selectedId={selected}
            onSelectRow={onSubSelect}
            containerClassName="!w-full"
            emptyStatement="No users match this search."
            emptyGuidance="Clear the filter to see everyone."
            emptyGlyph="results"
            render={(item) => {
              const fullName = `${item?.profile?.first_name} ${item?.profile?.last_name}`;
              return (
                <div className="flex items-center gap-3 px-4 py-3 w-full">
                  <ThumbIcon className="h-9 w-9 rounded-full" alt={fullName} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium capitalize truncate">
                      {fullName}
                    </span>
                    <span className="text-xs text-neutral-500 lowercase truncate">
                      {item?.email}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="p-3 border-t border-neutral-200 bg-neutral-50 space-y-3">
          {aclHelper(
            'user',
            'w',
            <Button
              primary
              icon={Plus}
              className="w-full"
              onClick={handleAddUser}
            >
              Add user
            </Button>,
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-500 tabular-nums">
              {users?.length || 0} of {meta?.total || 0}
            </p>
            <Pagination onChange={setFilter} value={meta} withPage={false} />
          </div>
        </div>
      </div>

      <Routes>
        <Route path=":id/*" element={<UserDetails users={users} />} />
        {users?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(users)?.uuid}`} />}
          />
        )}
      </Routes>
    </section>
  );
};

export default UserManagement;
